// Centralised TanStack Query keys + fetchers for the admin API.

import { api } from "./api";

// ─── Types (mirror @vinoga/db row types loosely; full types come when we wire @vinoga/db client-side) ─

export type Locale = "lv" | "en" | "ru";

export type ProductTranslation = {
  name: string;
  shortDescription?: string;
  description?: string;
};

export type CategoryTranslation = {
  name: string;
  description?: string;
};

export type Translations<T> = Partial<Record<Locale, T>>;

export type Product = {
  id: string;
  sku: string;
  slug: string;
  translations: Translations<ProductTranslation>;
  abv: string | null;
  volumeMl: number | null;
  vintage: number | null;
  country: string | null;
  region: string | null;
  producer: string | null;
  isAlcohol: boolean;
  requiresAgeVerification: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CategoryFeaturedTile = {
  eyebrow?: string;
  head?: string;
  cta?: string;
  ctaHref?: string;
  image?: string;
  bg?: string;
};

export type Category = {
  id: string;
  parentId: string | null;
  slug: string;
  translations: Translations<CategoryTranslation>;
  position: number;
  active: boolean;
  color: string | null;
  meta: { featured?: CategoryFeaturedTile };
};

export type InventoryLocation = {
  id: string;
  code: string;
  name: string;
  type: "shop" | "warehouse";
  address: string | null;
  isPickupPoint: boolean;
  active: boolean;
  position: number;
};

export type InventoryStock = {
  productId: string;
  locationId: string;
  qty: number;
  updatedAt: string;
};

export type ProductPrice = {
  productId: string;
  priceListId: string;
  amount: string;
  currency: string;
};

// ─── Fetchers ────────────────────────────────────────────────────────────

export async function listProducts(params: { q?: string; limit?: number; offset?: number } = {}) {
  const q = new URLSearchParams();
  if (params.q) q.set("q", params.q);
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  if (params.offset !== undefined) q.set("offset", String(params.offset));
  const suffix = q.toString() ? `?${q}` : "";
  return api<{ products: Product[]; total: number; limit: number; offset: number }>(
    `/api/v1/admin/products${suffix}`
  );
}

export async function getProduct(id: string) {
  return api<{
    product: Product;
    prices: ProductPrice[];
    categories: { productId: string; categoryId: string; position: number }[];
    images: { id: string; productId: string; url: string; alt: string | null; position: number }[];
    stock: InventoryStock[];
  }>(`/api/v1/admin/products/${id}`);
}

export async function listCategories() {
  return api<{ categories: Category[] }>("/api/v1/admin/categories");
}

export async function listInventoryLocations() {
  return api<{ locations: InventoryLocation[] }>("/api/v1/admin/inventory/locations");
}

// ─── Query keys ──────────────────────────────────────────────────────────

export const qk = {
  products: (q?: string) => ["products", { q: q ?? "" }] as const,
  product: (id: string) => ["product", id] as const,
  categories: () => ["categories"] as const,
  inventoryLocations: () => ["inventoryLocations"] as const,
};
